// src/pages/admin/security-dashboard.tsx
//
// Module de surveillance sécurité — visible uniquement par les admins
// (route protégée par <RequireAdmin> dans App.tsx).
//
// Données : supabase public.security_events + admin_audit_log + ip_blocklist

import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/layout/layout';
import { supabase } from '../../lib/supabase';
import {
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Ban,
  Activity,
  RefreshCw,
  Clock,
  User,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface SecurityEvent {
  id: string;
  event_type: string;
  ip_hash: string | null;
  details: Record<string, unknown> | null;
  organization_id: string | null;
  created_at: string;
}

interface AuditEntry {
  id: string;
  actor_id: string | null;
  action: string;
  table_name: string | null;
  record_id: string | null;
  created_at: string;
}

interface IpBlock {
  id: string;
  ip_hash: string;
  reason: string;
  auto_block: boolean;
  expires_at: string | null;
  created_at: string;
}

interface DashboardStats {
  events_24h: number;
  escalation_attempts_24h: number;
  rate_limit_hits_24h: number;
  csp_violations_24h: number;
  active_blocks: number;
  admin_actions_24h: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const EVENT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  role_escalation_attempt: { label: 'Escalade de rôle', color: 'text-red-600 bg-red-50' },
  rate_limit_exceeded:     { label: 'Rate limit', color: 'text-orange-600 bg-orange-50' },
  csp_violation:           { label: 'Violation CSP', color: 'text-yellow-700 bg-yellow-50' },
  wp_probe:                { label: 'Sonde WordPress', color: 'text-purple-600 bg-purple-50' },
  suspicious_ua:           { label: 'UA suspect', color: 'text-blue-600 bg-blue-50' },
  auth_failure:            { label: 'Auth échouée', color: 'text-red-500 bg-red-50' },
};

function eventBadge(type: string) {
  const meta = EVENT_TYPE_LABELS[type] ?? { label: type, color: 'text-neutral-600 bg-neutral-100' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
function StatCard({
  label,
  value,
  icon: Icon,
  color = 'text-neutral-700',
  bg = 'bg-white',
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  color?: string;
  bg?: string;
}) {
  return (
    <div className={`${bg} rounded-xl shadow border border-neutral-100 p-5 flex items-center gap-4`}>
      <div className={`${color} p-3 rounded-lg bg-current/10`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-neutral-900">{value}</p>
        <p className="text-xs text-neutral-500">{label}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function SecurityDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [blocklist, setBlocklist] = useState<IpBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [blockIpHash, setBlockIpHash] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [activeTab, setActiveTab] = useState<'events' | 'audit' | 'blocklist'>('events');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsRes, auditRes, blocklistRes, dashRes] = await Promise.all([
        supabase
          .from('security_events')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('admin_audit_log')
          .select('id, actor_id, action, table_name, record_id, created_at')
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('ip_blocklist')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase.from('security_dashboard').select('*').single(),
      ]);

      if (eventsRes.data) setEvents(eventsRes.data as SecurityEvent[]);
      if (auditRes.data) setAuditLog(auditRes.data as AuditEntry[]);
      if (blocklistRes.data) setBlocklist(blocklistRes.data as IpBlock[]);
      if (dashRes.data) setStats(dashRes.data as DashboardStats);

      setLastRefresh(new Date());
    } catch (err) {
      console.error('[security-dashboard] load error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh toutes les 60 s
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  const handleBlock = async () => {
    if (!blockIpHash.trim() || !blockReason.trim()) return;
    setBlocking(true);
    try {
      const { error } = await supabase.from('ip_blocklist').insert({
        ip_hash: blockIpHash.trim(),
        reason: blockReason.trim(),
        auto_block: false,
      });
      if (!error) {
        setBlockIpHash('');
        setBlockReason('');
        await load();
      }
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async (id: string) => {
    await supabase.from('ip_blocklist').delete().eq('id', id);
    await load();
  };

  const scoreColor =
    !stats
      ? 'text-neutral-400'
      : stats.escalation_attempts_24h > 0 || stats.rate_limit_hits_24h > 5
      ? 'text-red-600'
      : stats.events_24h > 10
      ? 'text-yellow-600'
      : 'text-green-600';

  const scoreLabel =
    !stats
      ? '—'
      : stats.escalation_attempts_24h > 0
      ? 'Critique'
      : stats.rate_limit_hits_24h > 5
      ? 'Dégradé'
      : stats.events_24h > 10
      ? 'Attention'
      : 'Nominal';

  return (
    <Layout title="Surveillance sécurité — Admin">
      <div className="container-custom py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Surveillance sécurité</h1>
              {lastRefresh && (
                <p className="text-xs text-neutral-400 flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />
                  Mis à jour {formatDate(lastRefresh.toISOString())}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {/* Score global */}
        <div className="bg-white rounded-xl border border-neutral-100 shadow p-6 mb-8 flex items-center gap-6">
          <ShieldAlert className={`w-10 h-10 ${scoreColor}`} />
          <div>
            <p className="text-xs uppercase tracking-widest text-neutral-400 font-bold mb-1">Statut global</p>
            <p className={`text-3xl font-black ${scoreColor}`}>{scoreLabel}</p>
          </div>
          <p className="ml-6 text-sm text-neutral-500 max-w-sm">
            Basé sur les tentatives d'escalade, les rate limits et le volume d'événements des 24 dernières heures.
          </p>
        </div>

        {/* Stats grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <StatCard label="Événements 24h" value={stats.events_24h} icon={Activity} />
            <StatCard
              label="Escalades rôle"
              value={stats.escalation_attempts_24h}
              icon={ShieldAlert}
              color={stats.escalation_attempts_24h > 0 ? 'text-red-600' : 'text-neutral-400'}
            />
            <StatCard
              label="Rate limits"
              value={stats.rate_limit_hits_24h}
              icon={AlertTriangle}
              color={stats.rate_limit_hits_24h > 5 ? 'text-orange-600' : 'text-neutral-400'}
            />
            <StatCard
              label="Violations CSP"
              value={stats.csp_violations_24h}
              icon={AlertTriangle}
              color={stats.csp_violations_24h > 0 ? 'text-yellow-600' : 'text-neutral-400'}
            />
            <StatCard
              label="IPs bloquées"
              value={stats.active_blocks}
              icon={Ban}
              color={stats.active_blocks > 0 ? 'text-red-600' : 'text-green-600'}
            />
            <StatCard
              label="Actions admin 24h"
              value={stats.admin_actions_24h}
              icon={User}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-neutral-200">
          {(['events', 'audit', 'blocklist'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-neutral-500 hover:text-neutral-800'
              }`}
            >
              {tab === 'events' && `Événements (${events.length})`}
              {tab === 'audit' && `Journal admin (${auditLog.length})`}
              {tab === 'blocklist' && `Blocklist IP (${blocklist.length})`}
            </button>
          ))}
        </div>

        {/* Events tab */}
        {activeTab === 'events' && (
          <div className="bg-white rounded-xl border border-neutral-100 shadow overflow-hidden">
            {events.length === 0 ? (
              <div className="py-16 text-center text-neutral-400">Aucun événement de sécurité.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-neutral-600">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-neutral-600">IP (hash)</th>
                    <th className="text-left px-4 py-3 font-semibold text-neutral-600">Détails</th>
                    <th className="text-left px-4 py-3 font-semibold text-neutral-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {events.map((e) => (
                    <tr key={e.id} className="hover:bg-neutral-50/50">
                      <td className="px-4 py-3">{eventBadge(e.event_type)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-neutral-500 truncate max-w-[120px]">
                        {e.ip_hash ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-600 max-w-xs truncate">
                        {e.details ? JSON.stringify(e.details) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-400 whitespace-nowrap">
                        {formatDate(e.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Audit log tab */}
        {activeTab === 'audit' && (
          <div className="bg-white rounded-xl border border-neutral-100 shadow overflow-hidden">
            {auditLog.length === 0 ? (
              <div className="py-16 text-center text-neutral-400">Aucune action admin enregistrée.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-neutral-600">Action</th>
                    <th className="text-left px-4 py-3 font-semibold text-neutral-600">Table</th>
                    <th className="text-left px-4 py-3 font-semibold text-neutral-600">ID enregistrement</th>
                    <th className="text-left px-4 py-3 font-semibold text-neutral-600">Acteur</th>
                    <th className="text-left px-4 py-3 font-semibold text-neutral-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {auditLog.map((e) => (
                    <tr key={e.id} className="hover:bg-neutral-50/50">
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                          e.action === 'DELETE'
                            ? 'bg-red-50 text-red-700'
                            : e.action === 'INSERT'
                            ? 'bg-green-50 text-green-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}>{e.action}</span>
                      </td>
                      <td className="px-4 py-3 text-neutral-600">{e.table_name ?? '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-neutral-500 truncate max-w-[100px]">
                        {e.record_id ?? '—'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-neutral-500 truncate max-w-[80px]">
                        {e.actor_id?.slice(0, 8) ?? 'service'}
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-400 whitespace-nowrap">
                        {formatDate(e.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Blocklist tab */}
        {activeTab === 'blocklist' && (
          <div className="space-y-6">
            {/* Add block form */}
            <div className="bg-white rounded-xl border border-neutral-100 shadow p-6">
              <h3 className="text-base font-bold text-neutral-800 mb-4">Bloquer une IP (hash SHA-256)</h3>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="text-xs text-neutral-500 font-medium mb-1 block">Hash de l'IP</label>
                  <input
                    type="text"
                    value={blockIpHash}
                    onChange={(e) => setBlockIpHash(e.target.value)}
                    placeholder="sha256(ip+salt)"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-neutral-500 font-medium mb-1 block">Motif</label>
                  <input
                    type="text"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="Ex: brute-force login"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                  />
                </div>
                <button
                  onClick={handleBlock}
                  disabled={blocking || !blockIpHash.trim() || !blockReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition disabled:opacity-50"
                >
                  {blocking ? 'Blocage…' : 'Bloquer'}
                </button>
              </div>
            </div>

            {/* List */}
            <div className="bg-white rounded-xl border border-neutral-100 shadow overflow-hidden">
              {blocklist.length === 0 ? (
                <div className="py-16 text-center text-neutral-400">Aucune IP bloquée.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-100">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-neutral-600">Hash IP</th>
                      <th className="text-left px-4 py-3 font-semibold text-neutral-600">Motif</th>
                      <th className="text-left px-4 py-3 font-semibold text-neutral-600">Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-neutral-600">Expire</th>
                      <th className="text-left px-4 py-3 font-semibold text-neutral-600">Date</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-50">
                    {blocklist.map((b) => (
                      <tr key={b.id} className="hover:bg-neutral-50/50">
                        <td className="px-4 py-3 font-mono text-xs text-neutral-500 truncate max-w-[120px]">
                          {b.ip_hash}
                        </td>
                        <td className="px-4 py-3 text-neutral-600 max-w-xs truncate">{b.reason}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            b.auto_block ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {b.auto_block ? 'Auto' : 'Manuel'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-neutral-400">
                          {b.expires_at ? formatDate(b.expires_at) : 'Permanent'}
                        </td>
                        <td className="px-4 py-3 text-xs text-neutral-400 whitespace-nowrap">
                          {formatDate(b.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleUnblock(b.id)}
                            className="text-xs text-red-500 hover:underline"
                          >
                            Débloquer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
